import {
  EmptyResponseMessage,
  SvcAddProductsToUserWishlist,
  SvcCreateUserWishlist,
  SvcDeleteUserWishlist,
  SvcGetProductAvailability,
  SvcGetUserWishlist,
  SvcListUserWishlists,
  SvcRemoveProductsFromUserWishlist,
  SvcUpdateUserWishlist,
  UsersWishlistsListUserWishlistsResponse_Wishlist,
} from "@springtree/eva-services-core";
import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { createContext, useContext, useMemo } from "react";

import { ProductActionOutcome } from "~/components/common/product-actions";
import { useAuthContext } from "~/contexts/auth";
import { useEvaService } from "~/hooks/use-eva-service";
import { wishlistIdType, wishlistNameType } from "~/shared/wishlist/wishlist.model";
import { ListProductTrackingInfo } from "~/types/trackings.models";
import Constants from "~/utils/constants";
import TrackingUtils from "~/utils/tracking-utils";

import { useConfiguration } from "./configuration";

export type CompleteWishlist = UsersWishlistsListUserWishlistsResponse_Wishlist & { Products?: any[]; count: number };

type UpdateWishlistProps = {
  wishlistName: wishlistNameType;
  wishlistId: wishlistIdType;
  onSuccess?: () => void;
  onError?: (error?: Error) => void;
};
type DeleteWishlistProps = {
  wishlistId: wishlistIdType;
  onSuccess?: () => void;
  onError?: (error?: Error) => void;
};

type WishlistContext = {
  list: CompleteWishlist[] | undefined;
  products: any[] | undefined;
  refetch: () => Promise<any>;
  updateWishlist: (data: UpdateWishlistProps) => Promise<EmptyResponseMessage | undefined>;
  deleteWishlist: (data: DeleteWishlistProps) => Promise<EmptyResponseMessage | undefined>;
  toggleWishlist: (
    productId: string,
    wishlistId: string,
    updatedList?: CompleteWishlist[],
    trackingData?: ListProductTrackingInfo
  ) => Promise<any>;
  removeProdFromWishlist: (
    productId: string,
    wishlistId: string,
    trackingData?: ListProductTrackingInfo
  ) => Promise<any>;
  addProdToWishlist: (productId: string, wishlistId: string, trackingData?: ListProductTrackingInfo) => Promise<any>;
  saveForLater: (productIDs: string | string[]) => Promise<any>;
  savedForLaterList: CompleteWishlist | undefined;
};

const Context = createContext<WishlistContext | undefined>(undefined);

function _trackWishlistEcomAction(
  action: ProductActionOutcome,
  quantity: number,
  trackingData?: ListProductTrackingInfo
) {
  if (trackingData) {
    if (trackingData.info) {
      trackingData.info.quantity = quantity;
    }

    const trackPayload = TrackingUtils.mapBasketAction(
      "wishlist",
      action,
      trackingData.context ?? "",
      trackingData.info
    );
    trackPayload && TrackingUtils.track(trackPayload);
  }
}

export default function WishlistProvider(props: { children: React.ReactNode }) {
  const t = useTranslations();
  const { eva } = useConfiguration();
  const { user, isAuthenticated } = useAuthContext();

  const listWishlists = useEvaService(SvcListUserWishlists);
  const getUserWishlist = useEvaService(SvcGetUserWishlist);
  const deleteWishlistHook = useEvaService(SvcDeleteUserWishlist);
  const updateWishlistHook = useEvaService(SvcUpdateUserWishlist);
  const addProductToWishlist = useEvaService(SvcAddProductsToUserWishlist);
  const removeProductFromWishlist = useEvaService(SvcRemoveProductsFromUserWishlist);
  const createWishlist = useEvaService(SvcCreateUserWishlist);

  const { data, refetch } = useQuery({
    queryKey: ["getWishlists", user?.ID],
    staleTime: 0,
    queryFn: async () => {
      const allWishlists = (await listWishlists())?.Results ?? [];
      return await Promise.all(
        allWishlists.map(async ({ ID }) => {
          const wishlist = await getUserWishlist({ ID: ID });
          const products = wishlist?.Products?.filter(Boolean) ?? [];
          if (products.length) {
            const availabilities = await eva.getProductAvailabilityDeliveryOnly(
              products.map((p) => ({ ID: p.product_id as string }))
            );

            for (const product of products) {
              const productAvailability = availabilities?.Products?.find(
                (p) => `${p.ProductID}` === `${product.product_id}`
              );

              product.is_available = productAvailability?.Delivery?.IsAvailable || false;
              product.has_stock = productAvailability?.Delivery?.HasStock || false;
            }
          }

          return { ...wishlist, Products: products, count: products.length } as CompleteWishlist;
        })
      );
    },
    enabled: isAuthenticated && !!user?.ID,
  });

  const filteredList = useMemo(() => {
    if (data) {
      return data.filter((list) => list.Name !== Constants.SAVE_FOR_LATER_WISHLIST_NAME);
    }
  }, [data]);

  const products = useMemo(() => {
    if (filteredList) {
      return filteredList.flatMap((list) => list.Products);
    }
  }, [filteredList]);

  const savedForLaterList = useMemo(() => {
    if (data) {
      return data.find((list) => list.Name === Constants.SAVE_FOR_LATER_WISHLIST_NAME);
    }
  }, [data]);

  const updateWishlist = async (data: UpdateWishlistProps) => {
    try {
      const result = await updateWishlistHook({
        ID: data.wishlistId,
        Name: data.wishlistName,
      });
      if (typeof data.onSuccess === "function") {
        data.onSuccess();
      }
      return result;
    } catch (error) {
      if (typeof data.onError === "function") {
        data.onError(error as Error);
      }
      throw error;
    }
  };

  const deleteWishlist = async (data: DeleteWishlistProps) => {
    try {
      const result = await deleteWishlistHook({
        ID: data.wishlistId,
      });
      if (typeof data.onSuccess === "function") {
        data.onSuccess();
      }
      return result;
    } catch (error) {
      if (typeof data.onError === "function") {
        data.onError(error as Error);
      }
      throw error;
    }
  };

  const removeProdFromWishlist = async (
    productId: string,
    wishlistId: string,
    trackingData?: ListProductTrackingInfo
  ) => {
    const selectedWishlist = data?.find((list) => `${list.ID}` == `${wishlistId}`);
    if (selectedWishlist) {
      const alreadyExist = selectedWishlist.Products?.find((prod) => `${prod.product_id}` === `${productId}`);
      if (alreadyExist) {
        return await removeProductFromWishlist({ ID: wishlistId, ProductIDs: [productId] }).then(() => {
          _trackWishlistEcomAction("removed", 1, trackingData);
          return refetch();
        });
      } else {
        return { errorMessage: "is not in your wishlist" };
      }
    }
  };

  const addProdToWishlist = async (productId: string, wishlistId: string, trackingData?: ListProductTrackingInfo) => {
    const selectedWishlist = data?.find((list) => `${list.ID}` == `${wishlistId}`);

    if (selectedWishlist) {
      const alreadyExist = selectedWishlist.Products?.find((prod) => `${prod.product_id}` === `${productId}`);
      if (alreadyExist) {
        return { errorMessage: t("generic.already_in_wishlist") };
      } else {
        return await addProductToWishlist({ ID: wishlistId, ProductIDs: [productId] }).then(() => {
          _trackWishlistEcomAction("added", 1, trackingData);
          return refetch();
        });
      }
    }
  };

  const toggleWishlist = async (
    productId: string,
    wishlistId: string,
    updatedList?: CompleteWishlist[],
    trackingData?: ListProductTrackingInfo
  ) => {
    const currentLists = updatedList ?? filteredList;
    const selectedWishlist = currentLists?.find((list) => `${list.ID}` == `${wishlistId}`);
    if (selectedWishlist) {
      const alreadyExist = selectedWishlist.Products?.find((prod) => `${prod.product_id}` === `${productId}`);
      if (alreadyExist) {
        return await removeProductFromWishlist({ ID: wishlistId, ProductIDs: [productId] }).then(() => {
          _trackWishlistEcomAction("removed", 1, trackingData);
          return refetch();
        });
      } else {
        return await addProductToWishlist({ ID: wishlistId, ProductIDs: [productId] }).then(() => {
          _trackWishlistEcomAction("added", 1, trackingData);
          return refetch();
        });
      }
    }
  };

  const saveForLater = async (productIDs: string | string[]) => {
    // Normalize the ProductIDs parameter into an array if it's not already an array
    let arrayedProductIDs = [];
    if (!Array.isArray(productIDs)) {
      arrayedProductIDs = [productIDs];
    } else {
      arrayedProductIDs = [...productIDs?.filter((item) => item !== undefined)];
    }

    // Filter any undefined values from the array (may happen in contexts of OOS products)
    if (!arrayedProductIDs?.length) return undefined;

    if (!savedForLaterList) {
      const response = await createWishlist({
        Name: Constants.SAVE_FOR_LATER_WISHLIST_NAME,
      });
      if (response?.ID) {
        return await addProductToWishlist({ ID: response?.ID, ProductIDs: [...arrayedProductIDs] }).then(() =>
          refetch()
        );
      }
    } else {
      return await addProductToWishlist({ ID: savedForLaterList.ID, ProductIDs: [...arrayedProductIDs] }).then(() =>
        refetch()
      );
    }
  };

  return (
    <Context.Provider
      value={{
        list: filteredList,
        products,
        updateWishlist,
        deleteWishlist,
        refetch,
        toggleWishlist,
        saveForLater,
        addProdToWishlist,
        removeProdFromWishlist,
        savedForLaterList,
      }}
    >
      {props.children}
    </Context.Provider>
  );
}

export function useWishlist() {
  const context = useContext(Context);

  if (!context) {
    throw new Error(`useWishlist must be used within a WishlistProvider`);
  }

  return context;
}
